import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import api from "../lib/api";
import { format, addDays, startOfWeek, isSameDay, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Plus, X, Clock, User, MapPin, GripVertical, CreditCard,
  CheckCircle2, AlertTriangle, CircleDot, PlayCircle, UserX, MoreVertical, Thermometer,
  CalendarPlus, RotateCcw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "../components/ui/dropdown-menu";
import AppointmentAuditLog from "../components/AppointmentAuditLog";
import FormulaPanel from "../components/FormulaPanel";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8am to 7pm

const ZOOM_LEVELS = [
  { pxPerHour: 32,  snap: 30, label: "30m" },
  { pxPerHour: 48,  snap: 15, label: "15m" },
  { pxPerHour: 64,  snap: 15, label: "15m" },
  { pxPerHour: 96,  snap: 10, label: "10m" },
  { pxPerHour: 128, snap: 5,  label: "5m"  },
  { pxPerHour: 192, snap: 5,  label: "5m"  },
];
const DEFAULT_ZOOM_IDX = 2; // 64px = default

function getEventStyle(startTime, endTime, pxPerHour) {
  const start = parseISO(startTime);
  const end = parseISO(endTime);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  // 8 AM = 480 min. Clamp so appointments before 8 AM don't bleed into the header.
  const rawTop = ((startMinutes - 480) / 60) * pxPerHour;
  const top = Math.max(rawTop, 0);
  const hiddenAbove = Math.max(-rawTop, 0);
  const height = Math.max(((endMinutes - startMinutes) / 60) * pxPerHour - hiddenAbove, 20);
  return { top: `${top}px`, height: `${height}px` };
}

const statusColors = {
  booked:    { bg: "#EFF6FF", border: "#3B82F6", text: "#1D4ED8", label: "Booked",     icon: "CircleDot" },
  confirmed: { bg: "#EFF6FF", border: "#3B82F6", text: "#1D4ED8", label: "Booked",     icon: "CircleDot" },
  late:      { bg: "#FFF7ED", border: "#F97316", text: "#C2410C", label: "Late",       icon: "AlertTriangle" },
  "in-service": { bg: "#F0FDF4", border: "#22C55E", text: "#15803D", label: "In Service", icon: "PlayCircle" },
  "no-show": { bg: "#FEF2F2", border: "#EF4444", text: "#B91C1C", label: "No-Show",   icon: "UserX" },
  completed: { bg: "#F0FDF4", border: "#16A34A", text: "#15803D", label: "Completed",  icon: "CheckCircle2" },
  cancelled: { bg: "#FEF2F2", border: "#DC2626", text: "#B91C1C", label: "Cancelled",  icon: "X" },
  pending:   { bg: "#F8FAFC", border: "#94A3B8", text: "#64748B", label: "Pending",    icon: "Clock" },
};

const STATUS_ICONS = { CircleDot, AlertTriangle, PlayCircle, UserX, CheckCircle2, X, Clock };

function StatusBadge({ status }) {
  const s = statusColors[status] || statusColors.booked;
  const Icon = STATUS_ICONS[s.icon] || CircleDot;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" style={{ backgroundColor: s.bg, color: s.text, border: `1px solid ${s.border}30` }}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2} />
      {s.label}
    </span>
  );
}

// Format "HH:MM" (24h) → "h:MM AM/PM" for shift labels
const fmtShiftTime = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${ampm}` : `${h12} ${ampm}`;
};

// Service category color mapping for appointment blocks
const SERVICE_CATEGORY_COLORS = {
  Hair:             { bg: "#DBEAFE", border: "#3B82F6", text: "#1E40AF" },
  Color:            { bg: "#F3E8FF", border: "#8B5CF6", text: "#6D28D9" },
  "Lashes & Brows": { bg: "#FCE7F3", border: "#EC4899", text: "#BE185D" },
  Nails:            { bg: "#FEE2E2", border: "#EF4444", text: "#B91C1C" },
  Skin:             { bg: "#D1FAE5", border: "#10B981", text: "#065F46" },
};

// Hex → rgba with alpha, for translucent gradient legend bubbles.
const hexToRgba = (hex, a = 1) => {
  const h = (hex || "#000").replace("#", "");
  const full = h.length === 3 ? h.split("").map(x => x + x).join("") : h;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// Glossy, translucent gradient "bubble" style for the legend swatches.
const legendBubbleStyle = (hex) => ({
  background: `radial-gradient(circle at 32% 26%, ${hexToRgba(hex, 0.95)} 0%, ${hexToRgba(hex, 0.62)} 52%, ${hexToRgba(hex, 0.34)} 100%)`,
  boxShadow: `inset 0 1px 1.5px rgba(255,255,255,0.7), inset 0 -1.5px 2.5px ${hexToRgba(hex, 0.35)}, 0 1px 3px ${hexToRgba(hex, 0.45)}`,
  border: `0.5px solid ${hexToRgba(hex, 0.45)}`,
});

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [schedules, setSchedules] = useState([]); // legacy weekly recurring schedules
  const [weekShifts, setWeekShifts] = useState([]); // date-specific shifts from Shift Builder
  const [ptoBlocks, setPtoBlocks] = useState([]); // approved time-off for visible week
  const [workingOnlyFilter, setWorkingOnlyFilter] = useState(true);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [apptTab, setApptTab] = useState("details");
  const [apptAuditKey, setApptAuditKey] = useState(0);
  // Map of {service_id: {custom_price, qualified}} for the currently-selected
  // provider in the appointment dialog. Used to surface per-staff pricing.
  const [providerQuals, setProviderQuals] = useState({});
  const [viewMode, setViewMode] = useState("week"); // week, day, or staff

  // Legend filters (empty set = show all)
  const [statusFilter, setStatusFilter] = useState(() => new Set());
  const [categoryFilter, setCategoryFilter] = useState(() => new Set());
  // Reschedule queue (clients flagged needs_reschedule)
  const [rescheduleQueue, setRescheduleQueue] = useState([]);
  const [showRescheduleQueue, setShowRescheduleQueue] = useState(false);
  // Sick-day dialog
  const [sickDayTarget, setSickDayTarget] = useState(null);
  const [sickNotify, setSickNotify] = useState(true);
  const [sickSubmitting, setSickSubmitting] = useState(false);

  // Drag-and-drop state
  const [draggedAppt, setDraggedAppt] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM_IDX);
  const calendarRef = useRef(null);
  const navigate = useNavigate();

  const zoom = ZOOM_LEVELS[zoomIdx];
  const pxPerHour = zoom.pxPerHour;
  const snapMinutes = zoom.snap;

  // Get service category color for an appointment
  const getCategoryColor = useCallback((appt) => {
    if (!appt?.service_ids?.length || !services.length) return null;
    const svc = services.find(s => s.service_id === appt.service_ids[0]);
    if (!svc) return null;
    return SERVICE_CATEGORY_COLORS[svc.category] || null;
  }, [services]);

  // Check for scheduling conflicts at a given time slot for a provider
  const getSlotConflicts = useCallback((staffId, day, hour, minute = 0) => {
    if (!draggedAppt || !staffId) return "free";
    const dayStr = format(day, "yyyy-MM-dd");
    const slotStart = new Date(day);
    slotStart.setHours(hour, minute, 0, 0);
    const duration = draggedAppt.end_time && draggedAppt.start_time
      ? parseISO(draggedAppt.end_time).getTime() - parseISO(draggedAppt.start_time).getTime()
      : 3600000;
    const slotEnd = new Date(slotStart.getTime() + duration);

    // Appointment overlap?
    const providerAppts = appointments.filter(a =>
      a.start_time?.startsWith(dayStr) &&
      a.provider_id === staffId &&
      a.appointment_id !== draggedAppt.appointment_id
    );
    for (const a of providerAppts) {
      const aStart = parseISO(a.start_time);
      const aEnd = parseISO(a.end_time);
      if (slotStart < aEnd && slotEnd > aStart) return "conflict";
    }

    // Off-hours check — is this within at least one of the provider's shifts today?
    const dayStr2 = format(day, "yyyy-MM-dd");
    const datedShifts = weekShifts.filter(sh =>
      sh.user_id === staffId && sh.date === dayStr2 &&
      (!sh.location_id || sh.location_id === selectedLocation)
    );
    const dow = (day.getDay() + 6) % 7;
    const todayShifts = datedShifts.length > 0 ? datedShifts : schedules.filter(sh =>
      sh.user_id === staffId && sh.location_id === selectedLocation &&
      sh.day_of_week === dow && sh.is_active !== false
    );
    if (todayShifts.length > 0) {
      const apStart = slotStart.getHours() * 60 + slotStart.getMinutes();
      const apEnd = slotEnd.getHours() * 60 + slotEnd.getMinutes();
      const withinAShift = todayShifts.some(s => {
        const [sh, sm] = s.start_time.split(":").map(Number);
        const [eh, em] = s.end_time.split(":").map(Number);
        return (sh * 60 + sm) <= apStart && apEnd <= (eh * 60 + em);
      });
      if (!withinAShift) return "offhours";
    }
    return "free";
  }, [appointments, draggedAppt, schedules, weekShifts, selectedLocation]);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  useEffect(() => {
    const load = async () => {
      try {
        const [locRes, staffRes, svcRes, cliRes, schRes] = await Promise.all([
          api.get("/locations"),
          api.get("/staff"),
          api.get("/services"),
          api.get("/clients?limit=200"),
          api.get("/schedules/weekly"),
        ]);
        setLocations(locRes.data);
        setStaff(staffRes.data);
        setServices(svcRes.data);
        setClients(cliRes.data.clients || []);
        setSchedules(schRes.data || []);
        if (locRes.data.length > 0) setSelectedLocation(locRes.data[0].location_id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    const fetchAppts = async () => {
      const startDate = format(weekStart, "yyyy-MM-dd");
      const endDate = format(addDays(weekStart, 7), "yyyy-MM-dd");
      try {
        const res = await api.get(`/appointments?location_id=${selectedLocation}&start_date=${startDate}T00:00:00&end_date=${endDate}T23:59:59`);
        setAppointments(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAppts();
  }, [selectedLocation, weekStart]);

  // Date-specific shifts created in the Shift Builder for the visible week.
  // These are the source of truth for who is working; the legacy weekly
  // `schedules` model remains as a fallback for older tenants.
  useEffect(() => {
    const fetchShifts = async () => {
      const startDate = format(weekStart, "yyyy-MM-dd");
      const endDate = format(addDays(weekStart, 6), "yyyy-MM-dd");
      try {
        const res = await api.get(`/shifts?start_date=${startDate}&end_date=${endDate}`);
        setWeekShifts(res.data || []);
      } catch {
        setWeekShifts([]);
      }
    };
    fetchShifts();
  }, [weekStart]);

  // ── Operations: scheduled tasks overlay ─────────────────────
  // Tasks that managers have time-boxed onto a stylist's calendar via
  // the "Find a free slot" flow in /operations. We fetch only the
  // currently-viewed day so the rail stays focused and lightweight.
  const [scheduledTasks, setScheduledTasks] = useState([]);
  useEffect(() => {
    const fetchTasks = async () => {
      const dayStr = format(currentDate, "yyyy-MM-dd");
      try {
        const res = await api.get("/tasks", { params: { scheduled_date: dayStr } });
        setScheduledTasks((res.data || []).filter(t => t.scheduled_start_time));
      } catch (err) {
        console.error(err);
        setScheduledTasks([]);
      }
    };
    fetchTasks();
  }, [currentDate]);

  const unscheduleTask = async (task) => {
    try {
      await api.post(`/tasks/${task.task_id}/unschedule`);
      toast.success(`Removed "${task.title}" from calendar`);
      // refresh
      const dayStr = format(currentDate, "yyyy-MM-dd");
      const res = await api.get("/tasks", { params: { scheduled_date: dayStr } });
      setScheduledTasks((res.data || []).filter(t => t.scheduled_start_time));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unschedule");
    }
  };

  // When the provider changes inside the open appointment dialog, refresh
  // the qualification/pricing map so we can show *their* price per service
  // and recompute the total live.
  useEffect(() => {
    const pid = selectedAppt?.provider_id;
    if (!pid) { setProviderQuals({}); return; }
    let cancelled = false;
    api.get(`/staff/${pid}/qualifications`)
      .then(r => {
        if (cancelled) return;
        const map = {};
        (r.data || []).forEach(q => {
          if (q.qualified) map[q.service_id] = q;
        });
        setProviderQuals(map);
      })
      .catch(() => { if (!cancelled) setProviderQuals({}); });
    return () => { cancelled = true; };
  }, [selectedAppt?.provider_id]);

  // Effective price for a (provider, service) pair — uses staff's custom_price
  // when set, otherwise the menu base price.
  const effectivePrice = (svcId) => {
    const svc = services.find(s => s.service_id === svcId);
    if (!svc) return 0;
    const q = providerQuals[svcId];
    if (q && q.custom_price !== null && q.custom_price !== undefined) {
      return Number(q.custom_price);
    }
    return Number(svc.price || 0);
  };

  // Effective duration in minutes for a (provider, service) pair — uses
  // staff's custom_duration_minutes when set, otherwise the service's base
  // duration. Used to size the calendar block when picking a service.
  const effectiveDuration = (svcId) => {
    const svc = services.find(s => s.service_id === svcId);
    const base = Number(svc?.duration || 60);
    const q = providerQuals[svcId];
    if (q && q.custom_duration_minutes !== null && q.custom_duration_minutes !== undefined && q.custom_duration_minutes !== 0) {
      return Number(q.custom_duration_minutes);
    }
    return base;
  };

  // Live-computed total for the open dialog so the user sees the right number
  // before saving (the backend recomputes too, so they always agree).
  const dialogTotal = (selectedAppt?.service_ids || []).reduce(
    (sum, sid) => sum + effectivePrice(sid), 0,
  );

  // When provider qualifications load (e.g. after switching provider in the
  // dialog), auto-resize end_time to that staff's custom duration for the
  // currently-selected service so the calendar block reflects their pace.
  useEffect(() => {
    if (!selectedAppt?.start_time) return;
    const sid = selectedAppt?.service_ids?.[0];
    if (!sid) return;
    const svc = services.find(s => s.service_id === sid);
    if (!svc) return;
    const q = providerQuals[sid];
    const dur = (q && q.custom_duration_minutes !== null && q.custom_duration_minutes !== undefined && q.custom_duration_minutes !== 0)
      ? Number(q.custom_duration_minutes)
      : Number(svc.duration || 60);
    try {
      const start = parseISO(selectedAppt.start_time);
      const newEnd = new Date(start.getTime() + dur * 60000).toISOString();
      if (newEnd !== selectedAppt.end_time) {
        setSelectedAppt(p => p ? { ...p, end_time: newEnd } : p);
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerQuals, selectedAppt?.service_ids?.[0], selectedAppt?.provider_id]);

  // Approved time-off blocks for the visible week — greyed out on the staff/day columns
  useEffect(() => {
    const fetchPto = async () => {
      const startDate = format(weekStart, "yyyy-MM-dd");
      const endDate = format(addDays(weekStart, 6), "yyyy-MM-dd");
      try {
        const res = await api.get(`/time-off/blocks?start_date=${startDate}&end_date=${endDate}`);
        setPtoBlocks(res.data || []);
      } catch { /* silent — endpoint may not exist on older tenants */ }
    };
    fetchPto();
  }, [weekStart]);

  // Return the PTO block (if any) covering a given staff member on a given date
  const ptoForStaffOnDay = (uid, date) => {
    const d = format(date, "yyyy-MM-dd");
    return ptoBlocks.find(b => b.user_id === uid && b.start_date <= d && b.end_date >= d) || null;
  };

  // Reusable refetchers so quick-actions (sick day, etc.) can refresh state.
  const refreshAppointments = useCallback(async () => {
    if (!selectedLocation) return;
    const startDate = format(weekStart, "yyyy-MM-dd");
    const endDate = format(addDays(weekStart, 7), "yyyy-MM-dd");
    try {
      const res = await api.get(`/appointments?location_id=${selectedLocation}&start_date=${startDate}T00:00:00&end_date=${endDate}T23:59:59`);
      setAppointments(res.data);
    } catch { /* silent */ }
  }, [selectedLocation, weekStart]);

  const refreshPto = useCallback(async () => {
    const startDate = format(weekStart, "yyyy-MM-dd");
    const endDate = format(addDays(weekStart, 6), "yyyy-MM-dd");
    try {
      const res = await api.get(`/time-off/blocks?start_date=${startDate}&end_date=${endDate}`);
      setPtoBlocks(res.data || []);
    } catch { /* silent */ }
  }, [weekStart]);

  // Reschedule queue — all clients flagged needs_reschedule (tenant-wide)
  const fetchRescheduleQueue = useCallback(async () => {
    try {
      const res = await api.get("/appointments/needs-reschedule");
      setRescheduleQueue(res.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchRescheduleQueue(); }, [fetchRescheduleQueue]);

  // Category name for an appointment (first service's category)
  const apptCategory = useCallback((appt) => {
    if (!appt?.service_ids?.length || !services.length) return null;
    const svc = services.find(s => s.service_id === appt.service_ids[0]);
    return svc?.category || null;
  }, [services]);

  // Is this a color/lightener appointment? (category "Color" or a color-ish
  // service name) — gates the "Formula" tab + log shortcut.
  const COLOR_KW = ["color", "colour", "lighten", "bleach", "balayage", "highlight", "foil", "toner", "glaze", "gloss", "ombre", "root", "tint"];
  const isColorAppt = useCallback((appt) => {
    if (!appt) return false;
    const cats = (appt.service_ids || []).map(id => (services.find(s => s.service_id === id)?.category || "").toLowerCase());
    if (cats.includes("color")) return true;
    const names = (appt.service_names || []).join(" ").toLowerCase();
    return COLOR_KW.some(k => names.includes(k));
  }, [services]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legend filter predicate (empty sets = everything passes)
  const passesLegend = useCallback((a) => {
    if (statusFilter.size && !statusFilter.has(a.status)) return false;
    if (categoryFilter.size) {
      const cat = apptCategory(a);
      if (!cat || !categoryFilter.has(cat)) return false;
    }
    return true;
  }, [statusFilter, categoryFilter, apptCategory]);

  const toggleStatusFilter = (key) => setStatusFilter(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });
  const toggleCategoryFilter = (cat) => setCategoryFilter(prev => {
    const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next;
  });
  const clearLegendFilters = () => { setStatusFilter(new Set()); setCategoryFilter(new Set()); };
  const legendActive = statusFilter.size > 0 || categoryFilter.size > 0;

  // Open the sick-day confirmation dialog for a staff member
  const openSickDayDialog = (staffMember) => { setSickNotify(true); setSickDayTarget(staffMember); };

  const confirmSickDay = async () => {
    if (!sickDayTarget) return;
    const dStr = format(currentDate, "yyyy-MM-dd");
    setSickSubmitting(true);
    try {
      const res = await api.post("/time-off/sick-day", { user_id: sickDayTarget.user_id, date: dStr, notify: sickNotify });
      const n = res.data?.appointments_flagged || 0;
      const notified = res.data?.notified || 0;
      const first = sickDayTarget.name.split(" ")[0];
      toast.success(
        `${first} marked out sick · ${n} appointment${n !== 1 ? "s" : ""} flagged` +
        (sickNotify ? ` · ${notified} client${notified !== 1 ? "s" : ""} emailed` : "")
      );
      setSickDayTarget(null);
      await Promise.all([refreshPto(), refreshAppointments(), fetchRescheduleQueue()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark sick day"); }
    finally { setSickSubmitting(false); }
  };

  const clearSickDay = async (staffMember) => {
    const dStr = format(currentDate, "yyyy-MM-dd");
    try {
      await api.post("/time-off/sick-day/clear", { user_id: staffMember.user_id, date: dStr });
      toast.success(`Sick day cleared for ${staffMember.name.split(" ")[0]}`);
      await Promise.all([refreshPto(), refreshAppointments(), fetchRescheduleQueue()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear sick day"); }
  };

  // Resolve a single reschedule-queue item (clear its flag)
  const resolveQueueItem = async (appointmentId) => {
    try {
      await api.post(`/appointments/${appointmentId}/clear-reschedule`);
      setRescheduleQueue(prev => prev.filter(a => a.appointment_id !== appointmentId));
      await refreshAppointments();
      toast.success("Marked as rescheduled");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  // Mon=0, Sun=6 — match backend convention
  const dayOfWeekForDate = (d) => (d.getDay() + 6) % 7;

  // Shifts for a specific staff+day at current location.
  // Date-specific Shift Builder shifts win; legacy weekly schedules are the fallback.
  const shiftsForStaffOnDay = useCallback((uid, date) => {
    const dStr = format(date, "yyyy-MM-dd");
    const dated = weekShifts.filter(sh =>
      sh.user_id === uid && sh.date === dStr &&
      (!sh.location_id || sh.location_id === selectedLocation)
    );
    if (dated.length > 0) return dated;
    const dow = dayOfWeekForDate(date);
    return schedules.filter(sh => sh.user_id === uid && sh.location_id === selectedLocation && sh.day_of_week === dow && sh.is_active !== false);
  }, [weekShifts, schedules, selectedLocation]);

  // Filter staff by location AND (in staff/day views with "working only") by whether
  // they are scheduled on the current day (Shift Builder shift OR legacy weekly
  // schedule) or already have appointments booked that day.
  const filteredStaff = useMemo(() => {
    const dStr = format(currentDate, "yyyy-MM-dd");
    // A staff member belongs on this location's calendar if their profile says
    // so OR the Shift Builder scheduled them here this week.
    const atLoc = staff.filter(s =>
      s.location_ids?.includes(selectedLocation) ||
      weekShifts.some(sh => sh.user_id === s.user_id && sh.location_id === selectedLocation)
    );
    if ((viewMode !== "staff" && viewMode !== "day") || !workingOnlyFilter) return atLoc;
    return atLoc.filter(s =>
      shiftsForStaffOnDay(s.user_id, currentDate).length > 0 ||
      appointments.some(a => a.provider_id === s.user_id && a.start_time?.startsWith(dStr) && a.status !== "cancelled")
    );
  }, [staff, selectedLocation, viewMode, workingOnlyFilter, shiftsForStaffOnDay, weekShifts, appointments, currentDate]);

  const openNewAppt = (staffMember, day, hour, minute = 0) => {
    const start = new Date(day);
    start.setHours(hour, minute, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 60);
    setSelectedAppt({
      isNew: true,
      provider_id: staffMember?.user_id || "",
      location_id: selectedLocation,
      client_id: "",
      service_ids: [],
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      status: "booked",
      notes: "",
    });
    setShowModal(true);
  };

  const openAppt = (appt) => {
    setSelectedAppt({ ...appt, isNew: false });
    setShowModal(true);
  };

  // Warn when a booking sits outside the provider's scheduled shift (or on a
  // day they aren't scheduled at all). Returns a human message or null.
  const outsideShiftWarning = useCallback((providerId, startIso, endIso) => {
    if (!providerId || !startIso) return null;
    const start = parseISO(startIso);
    const end = endIso ? parseISO(endIso) : start;
    // Only evaluate dates inside the fetched shift window (visible week)
    const dStr = format(start, "yyyy-MM-dd");
    if (dStr < format(weekStart, "yyyy-MM-dd") || dStr > format(addDays(weekStart, 6), "yyyy-MM-dd")) return null;
    const name = staff.find(s => s.user_id === providerId)?.name || "This stylist";
    const shifts = shiftsForStaffOnDay(providerId, start);
    if (shifts.length === 0) {
      // Only flag "not scheduled" when the tenant actually uses scheduling
      if (weekShifts.length === 0 && schedules.length === 0) return null;
      return `${name} isn't scheduled to work on ${format(start, "EEE, MMM d")}.`;
    }
    const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    const apStart = start.getHours() * 60 + start.getMinutes();
    const apEnd = end.getHours() * 60 + end.getMinutes();
    const within = shifts.some(s => toMin(s.start_time) <= apStart && apEnd <= toMin(s.end_time));
    if (within) return null;
    const range = shifts.map(s => `${fmtShiftTime(s.start_time)}–${fmtShiftTime(s.end_time)}`).join(", ");
    return `This time is outside ${name}'s scheduled shift (${range}) on ${format(start, "EEE, MMM d")}.`;
  }, [staff, shiftsForStaffOnDay, weekShifts, schedules, weekStart]);

  const saveAppt = async () => {
    if (!selectedAppt) return;
    const warning = outsideShiftWarning(selectedAppt.provider_id, selectedAppt.start_time, selectedAppt.end_time);
    if (warning && !window.confirm(`${warning}\n\nBook anyway?`)) return;
    try {
      if (selectedAppt.isNew) {
        await api.post("/appointments", {
          client_id: selectedAppt.client_id,
          provider_id: selectedAppt.provider_id,
          location_id: selectedAppt.location_id,
          service_ids: selectedAppt.service_ids,
          start_time: selectedAppt.start_time,
          end_time: selectedAppt.end_time,
          status: selectedAppt.status,
          notes: selectedAppt.notes,
          client_notes: selectedAppt.client_notes,
        });
      } else {
        await api.put(`/appointments/${selectedAppt.appointment_id}`, {
          client_id: selectedAppt.client_id,
          provider_id: selectedAppt.provider_id,
          service_ids: selectedAppt.service_ids,
          start_time: selectedAppt.start_time,
          end_time: selectedAppt.end_time,
          status: selectedAppt.status,
          notes: selectedAppt.notes,
          client_notes: selectedAppt.client_notes,
        });
      }
      // Refresh
      const startDate = format(weekStart, "yyyy-MM-dd");
      const endDate = format(addDays(weekStart, 7), "yyyy-MM-dd");
      const res = await api.get(`/appointments?location_id=${selectedLocation}&start_date=${startDate}T00:00:00&end_date=${endDate}T23:59:59`);
      setAppointments(res.data);
      setShowModal(false);
      setSelectedAppt(null);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteAppt = async () => {
    if (!selectedAppt?.appointment_id) return;
    try {
      await api.delete(`/appointments/${selectedAppt.appointment_id}`);
      setAppointments(prev => prev.filter(a => a.appointment_id !== selectedAppt.appointment_id));
      setShowModal(false);
      setSelectedAppt(null);
    } catch (err) {
      console.error(err);
    }
  };

  const checkoutAppt = () => {
    if (!selectedAppt) return;
    // Navigate to POS with appointment data pre-loaded
    const params = new URLSearchParams({
      appointment_id: selectedAppt.appointment_id || "",
      client_id: selectedAppt.client_id || "",
      client_name: selectedAppt.client_name || "",
      location_id: selectedAppt.location_id || selectedLocation || "",
      service_ids: (selectedAppt.service_ids || []).join(","),
      service_names: (selectedAppt.service_names || []).join(","),
      total_price: String(selectedAppt.total_price || 0),
      provider_id: selectedAppt.provider_id || "",
      provider_name: selectedAppt.provider_name || "",
    });
    setShowModal(false);
    navigate(`/pos?${params.toString()}`);
  };

  // ──── Drag-and-Drop Handlers ────
  const handleDragStart = (e, appt) => {
    e.stopPropagation();
    setDraggedAppt(appt);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", appt.appointment_id);
    // Make the drag image semi-transparent
    if (e.target) {
      e.target.style.opacity = "0.5";
      setTimeout(() => { if (e.target) e.target.style.opacity = "1"; }, 0);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, day, hour, targetProviderId, minute = 0) => {
    e.preventDefault();
    setIsDragging(false);
    if (!draggedAppt) return;
    try {
      const oldStart = parseISO(draggedAppt.start_time);
      const oldEnd = parseISO(draggedAppt.end_time);
      const durationMs = oldEnd.getTime() - oldStart.getTime();
      const newStart = new Date(day);
      newStart.setHours(hour, minute, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMs);
      const movedProviderId = targetProviderId || draggedAppt.provider_id;
      const warning = outsideShiftWarning(movedProviderId, newStart.toISOString(), newEnd.toISOString());
      if (warning && !window.confirm(`${warning}\n\nMove anyway?`)) {
        setDraggedAppt(null);
        return;
      }
      const updatePayload = {
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString(),
      };
      if (targetProviderId && targetProviderId !== draggedAppt.provider_id) {
        updatePayload.provider_id = targetProviderId;
        const newProvider = staff.find(s => s.user_id === targetProviderId);
        toast.success(`Moved ${draggedAppt.client_name} to ${newProvider?.name || "provider"} at ${format(newStart, "h:mm a")}`);
      } else {
        toast.success(`Moved ${draggedAppt.client_name} to ${format(newStart, "h:mm a")}`);
      }
      await api.put(`/appointments/${draggedAppt.appointment_id}`, updatePayload);
      // Refresh
      const startDate = format(weekStart, "yyyy-MM-dd");
      const endDate = format(addDays(weekStart, 7), "yyyy-MM-dd");
      const res = await api.get(`/appointments?location_id=${selectedLocation}&start_date=${startDate}T00:00:00&end_date=${endDate}T23:59:59`);
      setAppointments(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to reschedule";
      toast.error(msg);
    }
    setDraggedAppt(null);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedAppt(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const displayDays = viewMode === "day" ? [currentDate] : weekDays;
  const displayStaff = filteredStaff.length > 0 ? filteredStaff : [{ user_id: "all", name: "All Staff" }];

  return (
    <div className="h-screen flex flex-col" data-testid="calendar-page">
      {/* Calendar Header */}
      <div className="sticky top-0 z-20 bg-white/70 backdrop-blur-xl border-b border-black/5 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-['Outfit'] text-2xl font-medium tracking-tight text-slate-900">Schedule</h1>
            <div className="flex items-center gap-1 bg-[#FAFAFA] rounded-lg p-1 border border-slate-200">
              <button
                data-testid="view-day-btn"
                onClick={() => setViewMode("day")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "day" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              >Day</button>
              <button
                data-testid="view-week-btn"
                onClick={() => setViewMode("week")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "week" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              >Week</button>
              <button
                data-testid="view-staff-btn"
                onClick={() => setViewMode("staff")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "staff" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              >Staff</button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Location selector */}
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="location-select" className="w-[160px] h-9 border-slate-200 bg-white rounded-lg text-sm">
                <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400" />
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Nav */}
            <div className="flex items-center gap-2">
              <Button
                data-testid="prev-week-btn"
                variant="outline" size="icon"
                className="h-9 w-9 border-slate-200 rounded-lg"
                onClick={() => setCurrentDate(prev => addDays(prev, viewMode === "week" ? -7 : -1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <button
                data-testid="today-btn"
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-[#FAFAFA] rounded-lg transition-colors"
              >Today</button>
              <Button
                data-testid="next-week-btn"
                variant="outline" size="icon"
                className="h-9 w-9 border-slate-200 rounded-lg"
                onClick={() => setCurrentDate(prev => addDays(prev, viewMode === "week" ? 7 : 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <span className="text-sm font-medium text-slate-900 min-w-[140px]">
              {viewMode === "week"
                ? `${format(weekStart, "MMM d")} - ${format(addDays(weekStart, 6), "MMM d, yyyy")}`
                : format(currentDate, "EEEE, MMM d, yyyy")}
            </span>

            <Button
              data-testid="new-appointment-btn"
              onClick={() => openNewAppt(null, currentDate, 10)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-9 px-4 text-sm"
            >
              <Plus className="w-4 h-4 mr-1" /> New Booking
            </Button>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-[#FAFAFA] rounded-lg border border-slate-200 p-0.5" data-testid="zoom-controls">
              <button
                data-testid="zoom-out-btn"
                onClick={() => setZoomIdx(z => Math.max(0, z - 1))}
                disabled={zoomIdx === 0}
                className="w-8 h-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-white hover:shadow-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom out"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <span className="text-[10px] font-bold text-slate-500 w-8 text-center tabular-nums" data-testid="zoom-level">{zoom.label}</span>
              <button
                data-testid="zoom-in-btn"
                onClick={() => setZoomIdx(z => Math.min(ZOOM_LEVELS.length - 1, z + 1))}
                disabled={zoomIdx === ZOOM_LEVELS.length - 1}
                className="w-8 h-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-white hover:shadow-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom in"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Status Legend — grouped card with status icons (clickable filters) + queue/clear actions */}
      <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-slate-100 bg-white/70" data-testid="status-legend">
        <div className="flex items-center gap-2.5">
          <div className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400 pr-2 border-r border-slate-200">Status</span>
            {Object.entries(statusColors).filter(([k]) => ["booked","late","in-service","no-show","completed"].includes(k)).map(([key, c]) => {
              const Icon = STATUS_ICONS[c.icon] || CircleDot;
              const on = statusFilter.has(key);
              const dim = statusFilter.size > 0 && !on;
              return (
                <button
                  key={key}
                  data-testid={`legend-status-${key}`}
                  onClick={() => toggleStatusFilter(key)}
                  title={`Show only ${c.label}`}
                  className={`flex items-center gap-1.5 rounded-lg px-1.5 py-0.5 transition-all ${on ? "bg-slate-100 ring-1 ring-slate-300" : "hover:bg-slate-50"} ${dim ? "opacity-35" : ""}`}>
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-md flex-shrink-0" style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}40` }}>
                    <Icon className="w-2.5 h-2.5" strokeWidth={2.5} />
                  </span>
                  <span className="text-[11px] text-slate-600 font-['Manrope']">{c.label}</span>
                </button>
              );
            })}
          </div>
          {legendActive && (
            <button
              onClick={clearLegendFilters}
              data-testid="legend-clear-filters"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 rounded-full px-2.5 py-1 hover:bg-slate-100 transition-colors">
              <X className="w-3 h-3" /> Clear filter
            </button>
          )}
        </div>
        {rescheduleQueue.length > 0 && (
          <button
            onClick={() => setShowRescheduleQueue(true)}
            data-testid="reschedule-queue-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1.5 text-[11px] font-bold hover:bg-rose-100 transition-colors">
            <Thermometer className="w-3.5 h-3.5" strokeWidth={2.5} />
            Needs rescheduling
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px]">{rescheduleQueue.length}</span>
          </button>
        )}
      </div>

      {/* Category Color Legend (Staff View) + working-only toggle (Staff & Day views) */}
      {(viewMode === "staff" || viewMode === "day") && (
        <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-slate-200 bg-[#FAFAFA]" data-testid="category-legend">
          <div className="flex items-center gap-4">
            {viewMode === "staff" && (
              <div className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400 pr-2 border-r border-slate-200">Services</span>
                {Object.entries(SERVICE_CATEGORY_COLORS).map(([cat, c]) => {
                  const on = categoryFilter.has(cat);
                  const dim = categoryFilter.size > 0 && !on;
                  return (
                    <button
                      key={cat}
                      data-testid={`legend-service-${cat}`}
                      onClick={() => toggleCategoryFilter(cat)}
                      title={`Show only ${cat}`}
                      className={`flex items-center gap-1.5 rounded-lg px-1.5 py-0.5 transition-all ${on ? "bg-slate-100 ring-1 ring-slate-300" : "hover:bg-slate-50"} ${dim ? "opacity-35" : ""}`}>
                      <span className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={legendBubbleStyle(c.border)} />
                      <span className="text-[11px] text-slate-600">{cat}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            onClick={() => setWorkingOnlyFilter(v => !v)}
            data-testid="working-only-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${workingOnlyFilter ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:text-slate-900"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${workingOnlyFilter ? "bg-emerald-400" : "bg-slate-300"}`} />
            {workingOnlyFilter ? "Working today only" : "Show all staff"}
          </button>
        </div>
      )}

      {/* Scheduled Tasks rail — operations-tasks scheduled onto today */}
      {scheduledTasks.length > 0 && (
        <div className="px-4 py-2 border-b border-yellow-200/60 bg-[#FFE01B]/15" data-testid="calendar-scheduled-tasks-rail">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900">
              Tasks scheduled · {format(currentDate, "MMM d")}
            </span>
            <span className="text-[10px] text-amber-700">{scheduledTasks.length}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {scheduledTasks.map(t => (
              <div
                key={t.task_id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md ring-1 ring-yellow-300 bg-white text-[11px] text-amber-900"
                title={`${t.title} · ${t.assignee_name} · ${t.category}`}
                data-testid={`calendar-scheduled-task-${t.task_id}`}
              >
                <Clock className="h-3 w-3" />
                <span className="font-medium">
                  {new Date(t.scheduled_start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </span>
                <span className="text-amber-700/80">·</span>
                <span className="truncate max-w-[180px]">{t.title}</span>
                <span className="text-amber-700/70">→ {t.assignee_name || "?"}</span>
                <button
                  onClick={() => unscheduleTask(t)}
                  className="ml-1 text-amber-400 hover:text-rose-600"
                  title="Remove from calendar"
                  data-testid={`calendar-unschedule-${t.task_id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto" ref={calendarRef}>
        <div className="flex">
          {/* Time column */}
          <div className="w-16 flex-shrink-0 border-r border-slate-200">
            <div className={viewMode === "staff" ? "h-[60px] sticky top-0 z-30 bg-white border-b border-slate-200" : "h-12"} />
            {HOURS.map(hour => (
              <div key={hour} className="relative" style={{ height: `${pxPerHour}px` }}>
                <span className="absolute right-2 -top-2 text-xs text-slate-400">
                  {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                </span>
              </div>
            ))}
          </div>

          {/* ── Week View ── */}
          {viewMode === "week" && displayDays.map((day, dayIdx) => {
            const dayStr = format(day, "yyyy-MM-dd");
            const dayAppts = appointments.filter(a => a.start_time && a.start_time.startsWith(dayStr) && passesLegend(a));
            const isToday = isSameDay(day, new Date());
            return (
              <div key={dayIdx} className="flex-1 min-w-[120px] border-r border-slate-200 last:border-r-0">
                <div className={`h-12 flex items-center justify-center border-b border-slate-200 ${isToday ? 'bg-slate-900' : 'bg-[#FAFAFA]'}`}>
                  <div className="text-center">
                    <span className={`text-xs font-medium ${isToday ? 'text-white/70' : 'text-slate-400'}`}>{format(day, "EEE")}</span>
                    <span className={`ml-1.5 text-sm font-semibold ${isToday ? 'text-white' : 'text-slate-900'}`}>{format(day, "d")}</span>
                  </div>
                </div>
                <div className="relative">
                  {HOURS.map(hour => {
                    const slotsPerHour = 60 / snapMinutes;
                    return (
                      <div key={hour} className="relative" style={{ height: `${pxPerHour}px` }}>
                        {Array.from({ length: slotsPerHour }, (_, si) => {
                          const min = si * snapMinutes;
                          return (
                            <div key={si}
                              className={`absolute left-0 right-0 border-b cursor-pointer transition-colors ${
                                si === 0 ? 'border-slate-200/60' : 'border-slate-100/40'
                              } ${isDragging ? 'bg-[#FAFAFA]/30' : 'hover:bg-rose-50/30'}`}
                              style={{ top: `${(min / 60) * pxPerHour}px`, height: `${pxPerHour / slotsPerHour}px` }}
                              onClick={() => openNewAppt(null, day, hour, min)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, day, hour, null, min)}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                  {dayAppts.map(appt => {
                    const style = getEventStyle(appt.start_time, appt.end_time, pxPerHour);
                    const colors = statusColors[appt.status] || statusColors.booked;
                    const staffMember = staff.find(s => s.user_id === appt.provider_id);
                    const beingDragged = draggedAppt?.appointment_id === appt.appointment_id;
                    return (
                      <div key={appt.appointment_id} data-testid={`appointment-${appt.appointment_id}`}
                        className={`calendar-event ${beingDragged ? 'opacity-40 scale-95' : ''} ${isDragging ? 'pointer-events-none' : ''}`} draggable
                        onDragStart={(e) => handleDragStart(e, appt)} onDragEnd={handleDragEnd}
                        style={{ ...style, backgroundColor: colors.bg, borderLeftColor: colors.border, color: colors.text, cursor: 'grab' }}
                        onClick={(e) => { e.stopPropagation(); openAppt(appt); }}>
                        <div className="flex items-start gap-0.5">
                          <GripVertical className="w-3 h-3 opacity-30 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 mb-0.5">
                              <p className="text-[11px] font-semibold truncate">{appt.client_name}</p>
                              {parseInt(style.height) > 30 && <StatusBadge status={appt.status} />}
                            </div>
                            {parseInt(style.height) > 25 && <p className="text-[10px] truncate opacity-80">{appt.service_names?.join(", ")}</p>}
                            {parseInt(style.height) > 40 && <p className="text-[10px] opacity-60 truncate">{appt.provider_name}</p>}
                            {appt.notes && parseInt(style.height) > 55 && (
                              <p className="text-[9px] italic line-clamp-1 opacity-70 mt-0.5"
                                data-testid={`appt-notes-week-${appt.appointment_id}`}
                                title={appt.notes}>
                                “{appt.notes}”
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── Day View ── */}
          {viewMode === "day" && displayStaff.map((staffMember, sIdx) => {
            const dayStr = format(currentDate, "yyyy-MM-dd");
            const staffAppts = appointments.filter(a => a.start_time?.startsWith(dayStr) && (staffMember.user_id === "all" || a.provider_id === staffMember.user_id) && passesLegend(a));
            const pto = staffMember.user_id !== "all" ? ptoForStaffOnDay(staffMember.user_id, currentDate) : null;
            return (
              <div key={sIdx} className="flex-1 min-w-[160px] border-r border-slate-200 last:border-r-0">
                <div className="h-12 flex flex-col items-center justify-center border-b border-slate-200 bg-[#FAFAFA]">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: staffMember.color || '#B5A493' }} />
                    <span className="text-sm font-medium text-slate-900 truncate">{staffMember.name}</span>
                    {pto && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                        {pto.type === "vacation" ? "Vacation" : pto.type === "sick" ? "Sick" : "Personal"}
                      </span>
                    )}
                  </div>
                  {staffMember.user_id !== "all" && !pto && (() => {
                    const shs = shiftsForStaffOnDay(staffMember.user_id, currentDate);
                    if (shs.length === 0) return null;
                    return (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-600" data-testid={`day-shift-label-${staffMember.user_id}`}>
                        {shs.map(s => `${fmtShiftTime(s.start_time)}–${fmtShiftTime(s.end_time)}`).join(" · ")}
                      </span>
                    );
                  })()}
                </div>
                <div className="relative">
                  {/* Off-hours shading — grey out times outside this stylist's shift today */}
                  {(() => {
                    if (staffMember.user_id === "all" || pto) return null;
                    const todayShifts = shiftsForStaffOnDay(staffMember.user_id, currentDate);
                    if (!todayShifts.length) return null;
                    const gridStartMin = 8 * 60;
                    const gridEndMin = 20 * 60;
                    const working = todayShifts.map(s => {
                      const [sh, sm] = s.start_time.split(":").map(Number);
                      const [eh, em] = s.end_time.split(":").map(Number);
                      return { startM: sh * 60 + sm, endM: eh * 60 + em };
                    }).sort((a, b) => a.startM - b.startM);
                    const offBlocks = [];
                    let cursor = gridStartMin;
                    working.forEach(w => {
                      if (w.startM > cursor) offBlocks.push({ startM: cursor, endM: Math.min(w.startM, gridEndMin) });
                      cursor = Math.max(cursor, w.endM);
                    });
                    if (cursor < gridEndMin) offBlocks.push({ startM: cursor, endM: gridEndMin });
                    return offBlocks.map((b, i) => (
                      <div key={`dayoff-${i}`} data-testid={`day-offhours-${staffMember.user_id}-${i}`}
                        className="absolute left-0 right-0 pointer-events-none"
                        style={{
                          top: `${Math.max((b.startM - gridStartMin) / 60 * pxPerHour, 0)}px`,
                          height: `${Math.max((b.endM - b.startM) / 60 * pxPerHour, 0)}px`,
                          background: "repeating-linear-gradient(-45deg, rgba(148,163,184,0.08) 0, rgba(148,163,184,0.08) 6px, transparent 6px, transparent 12px)",
                          zIndex: 1,
                        }}
                      />
                    ));
                  })()}
                  {pto && (
                    <div
                      data-testid={`pto-overlay-day-${staffMember.user_id}`}
                      className="absolute left-0 right-0 pointer-events-none flex items-start justify-center pt-4"
                      style={{
                        top: 0,
                        height: `${(20 - 8) * pxPerHour}px`,
                        background: "repeating-linear-gradient(-45deg, rgba(148,163,184,0.18) 0, rgba(148,163,184,0.18) 8px, rgba(148,163,184,0.30) 8px, rgba(148,163,184,0.30) 10px)",
                        zIndex: 3,
                      }}>
                      <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-full px-3 py-1 shadow-sm">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 font-['Manrope']">
                          OOO · {pto.type === "vacation" ? "Vacation" : pto.type === "sick" ? "Sick" : "Personal"}
                        </span>
                      </div>
                    </div>
                  )}
                  {HOURS.map(hour => {
                    const slotsPerHour = 60 / snapMinutes;
                    return (
                      <div key={hour} className="relative" style={{ height: `${pxPerHour}px` }}>
                        {Array.from({ length: slotsPerHour }, (_, si) => {
                          const min = si * snapMinutes;
                          const slotKey = `day-${staffMember.user_id}-${hour}-${min}`;
                          const isHovered = isDragging && dropTarget?.key === slotKey;
                          return (
                            <div key={si}
                              className={`absolute left-0 right-0 border-b cursor-pointer transition-colors ${
                                si === 0 ? 'border-slate-200/60' : 'border-slate-100/40'
                              } ${isHovered ? 'bg-emerald-50' : isDragging ? 'bg-[#FAFAFA]/30' : 'hover:bg-rose-50/30'}`}
                              style={{ top: `${(min / 60) * pxPerHour}px`, height: `${pxPerHour / slotsPerHour}px` }}
                              onClick={() => openNewAppt(staffMember, currentDate, hour, min)}
                              onDragOver={(e) => {
                                handleDragOver(e);
                                setDropTarget({ staffId: staffMember.user_id, hour, min, key: slotKey });
                              }}
                              onDragLeave={() => setDropTarget(null)}
                              onDrop={(e) => { setDropTarget(null); handleDrop(e, currentDate, hour, staffMember.user_id === "all" ? null : staffMember.user_id, min); }}
                            >
                              {isHovered && (
                                <div className="absolute inset-x-1 inset-y-0 rounded border-2 border-dashed border-emerald-400 bg-emerald-50/50 flex items-center justify-center">
                                  <span className="text-[9px] font-bold uppercase text-emerald-500">
                                    {hour > 12 ? hour - 12 : hour}:{String(min).padStart(2, '0')}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {staffAppts.map(appt => {
                    const style = getEventStyle(appt.start_time, appt.end_time, pxPerHour);
                    const colors = statusColors[appt.status] || statusColors.booked;
                    const beingDragged = draggedAppt?.appointment_id === appt.appointment_id;
                    return (
                      <div key={appt.appointment_id} data-testid={`appointment-${appt.appointment_id}`}
                        className={`calendar-event ${beingDragged ? 'opacity-40 scale-95' : ''} ${isDragging ? 'pointer-events-none' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, appt)}
                        onDragEnd={() => { handleDragEnd(); setDropTarget(null); }}
                        style={{ ...style, backgroundColor: colors.bg, borderLeftColor: colors.border, color: colors.text, cursor: 'grab' }}
                        onClick={(e) => { e.stopPropagation(); openAppt(appt); }}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <p className="text-[11px] font-semibold truncate">{appt.client_name}</p>
                          {parseInt(style.height) > 30 && <StatusBadge status={appt.status} />}
                        </div>
                        {parseInt(style.height) > 25 && <p className="text-[10px] truncate opacity-80">{appt.service_names?.join(", ")}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── Staff Day View (Premium Salon View) ── */}
          {viewMode === "staff" && displayStaff.map((staffMember, sIdx) => {
            const dayStr = format(currentDate, "yyyy-MM-dd");
            const staffAppts = appointments.filter(a =>
              a.start_time?.startsWith(dayStr) &&
              (staffMember.user_id === "all" || a.provider_id === staffMember.user_id) &&
              passesLegend(a)
            );
            const initials = staffMember.name?.split(" ").map(n => n[0]).join("") || "?";
            const apptCount = staffAppts.length;
            const isDropTarget = isDragging && dropTarget?.staffId === staffMember.user_id;
            const firstName = staffMember.name?.split(" ")[0] || staffMember.name;

            return (
              <div key={sIdx} className={`flex-1 min-w-[120px] max-w-[240px] border-r border-slate-200 last:border-r-0 transition-colors ${isDropTarget ? 'bg-[#F0EDFF]' : ''}`}
                data-testid={`staff-column-${staffMember.user_id}`}>
                {/* Sticky Compact Header: color band + avatar + name + count */}
                <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]">
                  <div className="h-1" style={{ backgroundColor: staffMember.color || '#B5A493' }} />
                  <div className={`flex items-center gap-2 px-2.5 py-2 h-[52px] transition-colors ${isDropTarget ? 'bg-[#F0EDFF]' : 'bg-white'}`}>
                    <div className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
                      style={{ backgroundColor: staffMember.color || '#B5A493' }}>
                      {staffMember.picture ? (
                        <img src={staffMember.picture} alt={staffMember.name} className="w-full h-full rounded-full object-cover" />
                      ) : initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-slate-900 truncate leading-tight" title={staffMember.name}>{firstName}</p>
                      <p className="text-[9px] text-slate-400 uppercase tracking-wider font-bold leading-tight truncate">
                        {(() => {
                          const pto = staffMember.user_id !== "all" ? ptoForStaffOnDay(staffMember.user_id, currentDate) : null;
                          if (pto) {
                            const label = { vacation: "Vacation", sick: "Sick", personal: "Personal" }[pto.type] || "PTO";
                            return `OOO · ${label}`;
                          }
                          const shs = shiftsForStaffOnDay(staffMember.user_id, currentDate);
                          if (shs.length === 0) return apptCount > 0 ? `${apptCount} appt${apptCount !== 1 ? "s" : ""}` : "Free";
                          const range = shs.map(s => `${fmtShiftTime(s.start_time)}–${fmtShiftTime(s.end_time)}`).join(" · ");
                          return range;
                        })()}
                      </p>
                    </div>
                    {staffMember.user_id !== "all" && (() => {
                      const isSick = !!ptoForStaffOnDay(staffMember.user_id, currentDate);
                      return (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`staff-quick-actions-${staffMember.user_id}`}
                              onClick={(e) => e.stopPropagation()}
                              title="Quick actions"
                              className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                              <MoreVertical className="w-4 h-4" strokeWidth={2} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52" data-testid={`staff-quick-menu-${staffMember.user_id}`}>
                            <DropdownMenuLabel className="text-[11px] text-slate-400 font-semibold">
                              {staffMember.name?.split(" ")[0]} · {format(currentDate, "MMM d")}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid={`quick-new-appt-${staffMember.user_id}`}
                              onClick={() => openNewAppt(staffMember, currentDate, 9)}
                              className="cursor-pointer text-sm">
                              <CalendarPlus className="w-4 h-4 mr-2 text-slate-500" /> New appointment
                            </DropdownMenuItem>
                            {isSick ? (
                              <DropdownMenuItem
                                data-testid={`quick-clear-sick-${staffMember.user_id}`}
                                onClick={() => clearSickDay(staffMember)}
                                className="cursor-pointer text-sm">
                                <RotateCcw className="w-4 h-4 mr-2 text-slate-500" /> Clear sick day
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                data-testid={`quick-sick-day-${staffMember.user_id}`}
                                onClick={() => openSickDayDialog(staffMember)}
                                className="cursor-pointer text-sm text-rose-600 focus:text-rose-700 focus:bg-rose-50">
                                <Thermometer className="w-4 h-4 mr-2" /> Mark sick day
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })()}
                  </div>
                </div>
                {/* Time Grid */}
                <div className="relative">
                  {/* PTO overlay — full-day greyed "PTO" block when this stylist has approved time off today */}
                  {(() => {
                    if (staffMember.user_id === "all") return null;
                    const pto = ptoForStaffOnDay(staffMember.user_id, currentDate);
                    if (!pto) return null;
                    const gridStartMin = 8 * 60;
                    const gridEndMin = 20 * 60;
                    const totalHeight = (gridEndMin - gridStartMin) / 60 * pxPerHour;
                    const typeLabel = { vacation: "Vacation", sick: "Sick", personal: "Personal" }[pto.type] || "Time Off";
                    const tint = { vacation: "rgba(56,189,248,0.18)", sick: "rgba(244,63,94,0.16)", personal: "rgba(139,92,246,0.16)" }[pto.type] || "rgba(148,163,184,0.20)";
                    const stroke = { vacation: "rgba(56,189,248,0.28)", sick: "rgba(244,63,94,0.26)", personal: "rgba(139,92,246,0.26)" }[pto.type] || "rgba(148,163,184,0.30)";
                    return (
                      <div
                        data-testid={`pto-overlay-${staffMember.user_id}`}
                        className="absolute left-0 right-0 pointer-events-none flex flex-col items-center justify-center"
                        style={{
                          top: 0,
                          height: `${totalHeight}px`,
                          background: `repeating-linear-gradient(-45deg, ${tint} 0, ${tint} 8px, ${stroke} 8px, ${stroke} 10px)`,
                          zIndex: 3,
                        }}
                        title={`${typeLabel} — ${pto.start_date} to ${pto.end_date}`}>
                        <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-full px-3 py-1 shadow-sm flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stroke }} />
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 font-['Manrope']">PTO · {typeLabel}</span>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Off-hours overlay — grey out times outside this stylist's shifts today */}
                  {(() => {
                    const todayShifts = shiftsForStaffOnDay(staffMember.user_id, currentDate);
                    if (!todayShifts.length || staffMember.user_id === "all") return null;
                    // Total height = 12 hours (8AM-8PM) * pxPerHour
                    const gridStartMin = 8 * 60;
                    const gridEndMin = 20 * 60;
                    const totalHeight = (gridEndMin - gridStartMin) / 60 * pxPerHour;
                    // Build intervals of "working" then compute "off" intervals as complement
                    const working = todayShifts.map(s => {
                      const [sh, sm] = s.start_time.split(":").map(Number);
                      const [eh, em] = s.end_time.split(":").map(Number);
                      return { startM: sh * 60 + sm, endM: eh * 60 + em };
                    }).sort((a, b) => a.startM - b.startM);
                    const offBlocks = [];
                    let cursor = gridStartMin;
                    working.forEach(w => {
                      if (w.startM > cursor) offBlocks.push({ startM: cursor, endM: Math.min(w.startM, gridEndMin) });
                      cursor = Math.max(cursor, w.endM);
                    });
                    if (cursor < gridEndMin) offBlocks.push({ startM: cursor, endM: gridEndMin });
                    return offBlocks.map((b, i) => {
                      const top = Math.max((b.startM - gridStartMin) / 60 * pxPerHour, 0);
                      const height = Math.max((b.endM - b.startM) / 60 * pxPerHour, 0);
                      return (
                        <div key={`off-${i}`} data-testid={`offhours-${staffMember.user_id}-${i}`}
                          className="absolute left-0 right-0 pointer-events-none"
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            background: "repeating-linear-gradient(-45deg, rgba(148,163,184,0.08) 0, rgba(148,163,184,0.08) 6px, transparent 6px, transparent 12px)",
                            zIndex: 1,
                          }}
                        />
                      );
                    });
                  })()}
                  {HOURS.map(hour => {
                    const slotsPerHour = 60 / snapMinutes;
                    return (
                      <div key={hour} className="relative" style={{ height: `${pxPerHour}px` }}>
                        {Array.from({ length: slotsPerHour }, (_, si) => {
                          const min = si * snapMinutes;
                          const slotKey = `${staffMember.user_id}-${hour}-${min}`;
                          const isHovered = isDragging && dropTarget?.key === slotKey;
                          const conflict = isHovered ? getSlotConflicts(staffMember.user_id, currentDate, hour, min) : null;
                          const isBad = conflict === "conflict" || conflict === "offhours";
                          return (
                            <div key={si}
                              className={`absolute left-0 right-0 cursor-pointer transition-colors ${
                                si === 0 ? 'border-b border-slate-200/60' : 'border-b border-slate-100/30'
                              } ${isHovered
                                ? isBad ? 'bg-rose-50' : 'bg-emerald-50'
                                : isDragging ? 'bg-[#FAFAFA]/30' : 'hover:bg-rose-50/20'
                              }`}
                              style={{ top: `${(min / 60) * pxPerHour}px`, height: `${pxPerHour / slotsPerHour}px` }}
                              onClick={() => openNewAppt(staffMember, currentDate, hour, min)}
                              onDragOver={(e) => {
                                handleDragOver(e);
                                setDropTarget({ staffId: staffMember.user_id, hour, min, key: slotKey });
                              }}
                              onDragLeave={() => setDropTarget(null)}
                              onDrop={(e) => { setDropTarget(null); handleDrop(e, currentDate, hour, staffMember.user_id, min); }}
                            >
                              {isHovered && (
                                <div className={`absolute inset-x-1 inset-y-0 rounded border-2 border-dashed flex items-center justify-center ${
                                  isBad ? 'border-rose-400 bg-rose-50/70' : 'border-emerald-400 bg-emerald-50/50'
                                }`}>
                                  <span className={`text-[9px] font-bold uppercase ${isBad ? 'text-rose-500' : 'text-emerald-500'}`}>
                                    {conflict === "conflict" ? "Conflict" : conflict === "offhours" ? "Off hours" : `${hour > 12 ? hour-12 : hour}:${String(min).padStart(2,'0')}`}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {/* Appointment Blocks */}
                  {staffAppts.map(appt => {
                    const style = getEventStyle(appt.start_time, appt.end_time, pxPerHour);
                    const catColor = getCategoryColor(appt);
                    const startT = parseISO(appt.start_time);
                    const endT = parseISO(appt.end_time);
                    const beingDragged = draggedAppt?.appointment_id === appt.appointment_id;
                    const bgColor = catColor ? catColor.bg : (staffMember.color ? `${staffMember.color}18` : "#EAF0F6");
                    const borderColor = appt.needs_reschedule ? "#E11D48" : (catColor ? catColor.border : (staffMember.color || "#274F7D"));
                    const textColor = catColor ? catColor.text : "#1A1A1A";
                    return (
                      <div
                        key={appt.appointment_id}
                        data-testid={`staff-appt-${appt.appointment_id}`}
                        className={`absolute left-1 right-1 rounded-lg px-2.5 py-1.5 overflow-hidden cursor-grab border-l-[3px] transition-all ${
                          beingDragged ? 'opacity-30 scale-95 rotate-1' : 'hover:shadow-md hover:-translate-y-px'
                        } ${isDragging ? 'pointer-events-none' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, appt)}
                        onDragEnd={() => { handleDragEnd(); setDropTarget(null); }}
                        style={{
                          ...style,
                          backgroundColor: bgColor,
                          borderLeftColor: borderColor,
                        }}
                        onClick={(e) => { e.stopPropagation(); openAppt(appt); }}
                      >
                        {appt.status && appt.status !== "completed" && appt.status !== "cancelled" && (
                          <div className="absolute top-1 right-1">
                            <StatusBadge status={appt.status} />
                          </div>
                        )}
                        {appt.needs_reschedule && (
                          <div
                            data-testid={`appt-reschedule-${appt.appointment_id}`}
                            title="Provider out sick — reschedule this client"
                            className="absolute bottom-1 right-1 flex items-center gap-0.5 bg-rose-500 text-white rounded-full px-1.5 py-0.5 shadow-sm">
                            <Thermometer className="w-2.5 h-2.5" strokeWidth={2.5} />
                            <span className="text-[8px] font-bold uppercase tracking-wide">Move</span>
                          </div>
                        )}
                        <p className="text-[12px] font-semibold truncate leading-tight pr-16" style={{ color: textColor }}>{appt.client_name}</p>
                        <p className="text-[11px] truncate" style={{ color: `${textColor}CC` }}>{appt.service_names?.join(", ")}</p>
                        {parseInt(style.height) > 50 && (
                          <p className="text-[10px] mt-0.5" style={{ color: `${textColor}99` }}>
                            {format(startT, "h:mm a")} – {format(endT, "h:mm a")}
                          </p>
                        )}
                        {/* Appointment notes preview — only renders when card is tall enough */}
                        {appt.notes && parseInt(style.height) > 70 && (
                          <p className="text-[10px] italic leading-snug mt-1 line-clamp-2"
                            data-testid={`appt-notes-preview-${appt.appointment_id}`}
                            style={{ color: `${textColor}AA` }}
                            title={appt.notes}>
                            “{appt.notes}”
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Appointment Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) setApptTab("details"); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[560px] bg-white border-slate-200 rounded-2xl p-8 max-h-[90vh] overflow-y-auto" data-testid="appointment-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">
              {selectedAppt?.isNew ? "New Appointment" : "Edit Appointment"}
            </DialogTitle>
          </DialogHeader>
          {/* Tab strip — only on existing appointments (no audit history for unsaved drafts) */}
          {selectedAppt && !selectedAppt.isNew && (
            <div className="flex gap-1 mt-3 border-b border-slate-200" data-testid="appt-tabs">
              {[
                { key: "details", label: "Details" },
                { key: "activity", label: "Activity" },
                ...(isColorAppt(selectedAppt) ? [{ key: "formula", label: "Formula" }] : []),
              ].map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setApptTab(t.key); if (t.key === "activity") setApptAuditKey(k => k + 1); }}
                  data-testid={`appt-tab-${t.key}`}
                  className={`px-4 py-2 text-sm font-medium font-['Manrope'] -mb-px border-b-2 transition-colors ${
                    apptTab === t.key
                      ? "border-rose-500 text-slate-900"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {selectedAppt && apptTab === "activity" && !selectedAppt.isNew && (
            <div className="mt-4">
              <AppointmentAuditLog
                appointmentId={selectedAppt.appointment_id}
                refreshKey={apptAuditKey} />
            </div>
          )}
          {selectedAppt && apptTab === "formula" && !selectedAppt.isNew && (
            <div className="mt-4" data-testid="appt-formula-tab">
              <FormulaPanel
                clientId={selectedAppt.client_id}
                clientName={selectedAppt.client_name}
                appointmentId={selectedAppt.appointment_id} />
            </div>
          )}
          {selectedAppt && apptTab === "details" && (
            <div className="space-y-4 mt-4">
              {/* Existing client-notes preview banner — shows whenever the
                  selected client already has notes on file, so the staff
                  member sees "allergic to PPD" before they even start. */}
              {selectedAppt.client_notes && (
                <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="client-notes-banner">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1">Client notes on file</p>
                  <p className="text-xs text-amber-900 leading-relaxed whitespace-pre-wrap">{selectedAppt.client_notes}</p>
                </div>
              )}
              {/* Client */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block">Client</label>
                <Select
                  value={selectedAppt.client_id}
                  onValueChange={(v) => setSelectedAppt(p => ({ ...p, client_id: v }))}
                >
                  <SelectTrigger data-testid="appt-client-select" className="h-11 border-slate-200 bg-white rounded-lg">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Provider */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block">Provider</label>
                <Select
                  value={selectedAppt.provider_id}
                  onValueChange={(v) => setSelectedAppt(p => ({ ...p, provider_id: v }))}
                >
                  <SelectTrigger data-testid="appt-provider-select" className="h-11 border-slate-200 bg-white rounded-lg">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredStaff.map(s => (
                      <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Service */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block">Service</label>
                <Select
                  value={selectedAppt.service_ids?.[0] || ""}
                  onValueChange={(v) => {
                    const q = providerQuals[v];
                    const svc = services.find(s => s.service_id === v);
                    const base = Number(svc?.duration || 60);
                    const dur = (q && q.custom_duration_minutes !== null && q.custom_duration_minutes !== undefined && q.custom_duration_minutes !== 0)
                      ? Number(q.custom_duration_minutes) : base;
                    setSelectedAppt(p => {
                      const start = parseISO(p.start_time);
                      const end = new Date(start.getTime() + dur * 60000);
                      return { ...p, service_ids: [v], end_time: end.toISOString() };
                    });
                  }}
                >
                  <SelectTrigger data-testid="appt-service-select" className="h-11 border-slate-200 bg-white rounded-lg">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => {
                      const q = providerQuals[s.service_id];
                      const eff = q && q.custom_price !== null && q.custom_price !== undefined ? Number(q.custom_price) : null;
                      const effDur = q && q.custom_duration_minutes !== null && q.custom_duration_minutes !== undefined && q.custom_duration_minutes !== 0
                        ? Number(q.custom_duration_minutes) : null;
                      const dispDur = effDur !== null ? effDur : s.duration;
                      const isCustomDur = effDur !== null && effDur !== Number(s.duration);
                      return (
                        <SelectItem key={s.service_id} value={s.service_id}>
                          {s.name} ({dispDur}min{isCustomDur ? <span className="text-rose-500"> · custom</span> : null} – {eff !== null ? `$${eff}` : `$${s.price}`}{eff !== null && eff !== Number(s.price) ? <span className="text-rose-500"> · custom</span> : null})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedAppt.service_ids?.length > 0 && selectedAppt.provider_id && (
                  <div
                    data-testid="appt-effective-total"
                    className="mt-2 flex items-center justify-between text-xs font-['Manrope']">
                    <span className="text-slate-400">
                      {(() => {
                        const sid = selectedAppt.service_ids[0];
                        const svc = services.find(s => s.service_id === sid);
                        const q = providerQuals[sid];
                        const isCustom = q && q.custom_price !== null && q.custom_price !== undefined && Number(q.custom_price) !== Number(svc?.price || 0);
                        return isCustom ? "Custom price for this stylist" : "Standard service price";
                      })()}
                    </span>
                    <span className="text-slate-700 font-semibold tabular-nums">${dialogTotal.toFixed(2)}</span>
                  </div>
                )}
              </div>
              {/* Date/Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1.5 block">Date</label>
                  <Input
                    data-testid="appt-date-input"
                    type="date"
                    value={selectedAppt.start_time?.split("T")[0]}
                    onChange={(e) => {
                      const d = e.target.value;
                      const startTime = selectedAppt.start_time?.split("T")[1] || "10:00:00.000Z";
                      const endTime = selectedAppt.end_time?.split("T")[1] || "11:00:00.000Z";
                      setSelectedAppt(p => ({ ...p, start_time: `${d}T${startTime}`, end_time: `${d}T${endTime}` }));
                    }}
                    className="h-11 border-slate-200 bg-white rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1.5 block">Time</label>
                  <Input
                    data-testid="appt-time-input"
                    type="time"
                    value={selectedAppt.start_time ? format(parseISO(selectedAppt.start_time), "HH:mm") : ""}
                    onChange={(e) => {
                      const t = e.target.value;
                      const date = selectedAppt.start_time?.split("T")[0];
                      const sid = selectedAppt.service_ids?.[0];
                      const svc = services.find(s => s.service_id === sid);
                      const q = providerQuals[sid];
                      const dur = (q && q.custom_duration_minutes !== null && q.custom_duration_minutes !== undefined && q.custom_duration_minutes !== 0)
                        ? Number(q.custom_duration_minutes)
                        : (svc?.duration || 60);
                      const start = new Date(`${date}T${t}:00`);
                      const end = new Date(start.getTime() + dur * 60000);
                      setSelectedAppt(p => ({ ...p, start_time: start.toISOString(), end_time: end.toISOString() }));
                    }}
                    className="h-11 border-slate-200 bg-white rounded-lg"
                  />
                </div>
              </div>
              {/* Status */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block">Status</label>
                <Select
                  value={selectedAppt.status}
                  onValueChange={(v) => setSelectedAppt(p => ({ ...p, status: v }))}
                >
                  <SelectTrigger data-testid="appt-status-select" className="h-11 border-slate-200 bg-white rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="booked">Booked</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="in-service">In Service</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="no-show">No-Show</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Quick Status Actions */}
              {!selectedAppt.isNew && (
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-2 block">Quick Status</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { status: "booked", label: "Booked", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: CircleDot },
                      { status: "in-service", label: "In Service", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: PlayCircle },
                      { status: "late", label: "Late", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", icon: AlertTriangle },
                      { status: "no-show", label: "No-Show", bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", icon: UserX },
                      { status: "completed", label: "Completed", bg: "bg-green-50", text: "text-green-700", border: "border-green-200", icon: CheckCircle2 },
                    ].map(btn => (
                      <button key={btn.status} data-testid={`quick-status-${btn.status}`}
                        onClick={() => setSelectedAppt(p => ({ ...p, status: btn.status }))}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
                          selectedAppt.status === btn.status
                            ? `${btn.bg} ${btn.text} ${btn.border} ring-2 ring-offset-1`
                            : `bg-white text-slate-500 border-slate-200 hover:${btn.bg} hover:${btn.text}`
                        }`}
                        style={selectedAppt.status === btn.status ? { ringColor: statusColors[btn.status]?.border } : {}}
                      >
                        <btn.icon className="w-3.5 h-3.5" strokeWidth={2} />
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Appointment Notes — specific to THIS visit */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block flex items-center gap-1.5" data-testid="appt-notes-label">
                  Appointment Notes
                  <span className="text-[10px] text-slate-400 font-normal">For this visit only</span>
                </label>
                <textarea
                  data-testid="appt-notes-input"
                  value={selectedAppt.notes || ""}
                  onChange={(e) => setSelectedAppt(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Use 7N color, low foils around face..."
                  rows={2}
                  className="w-full p-3 border border-slate-200 bg-white rounded-lg text-sm focus:outline-none focus:border-rose-400 resize-y"
                />
              </div>
              {/* Client Notes — persistent on the client record */}
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block flex items-center gap-1.5" data-testid="client-notes-label">
                  Client Notes
                  <span className="text-[10px] text-slate-400 font-normal">Persists on the client across all visits</span>
                </label>
                <textarea
                  data-testid="client-notes-input"
                  value={selectedAppt.client_notes || ""}
                  onChange={(e) => setSelectedAppt(p => ({ ...p, client_notes: e.target.value }))}
                  placeholder="e.g. Allergic to PPD, prefers cooler tones..."
                  rows={2}
                  className="w-full p-3 border border-amber-200 bg-amber-50/30 rounded-lg text-sm focus:outline-none focus:border-amber-400 resize-y"
                />
              </div>
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  data-testid="save-appointment-btn"
                  onClick={saveAppt}
                  className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11"
                >
                  {selectedAppt.isNew ? "Book Appointment" : "Save Changes"}
                </Button>
                {!selectedAppt.isNew && (
                  <>
                    <Button
                      data-testid="checkout-appointment-btn"
                      onClick={checkoutAppt}
                      className="bg-emerald-600 text-white hover:bg-[#1f4027] rounded-full h-11 px-5"
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />Checkout
                    </Button>
                    <Button
                      data-testid="delete-appointment-btn"
                      onClick={deleteAppt}
                      variant="outline"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full h-11"
                    >
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sick Day confirmation dialog */}
      <Dialog open={!!sickDayTarget} onOpenChange={(o) => { if (!o) setSickDayTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="sick-day-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-rose-600" /> Mark {sickDayTarget?.name?.split(" ")[0]} out sick
            </DialogTitle>
          </DialogHeader>
          {sickDayTarget && (() => {
            const dStr = format(currentDate, "yyyy-MM-dd");
            const affected = appointments.filter(a => a.provider_id === sickDayTarget.user_id && a.start_time?.startsWith(dStr) && !["cancelled","completed","no-show"].includes(a.status));
            return (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 font-['Manrope']">
                  This blocks <b>{sickDayTarget.name.split(" ")[0]}</b>'s schedule on <b>{format(currentDate, "EEE, MMM d")}</b> and flags <b>{affected.length}</b> appointment{affected.length !== 1 ? "s" : ""} that day so the front desk knows to move those clients.
                </p>
                <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="sick-notify-toggle">
                  <input type="checkbox" checked={sickNotify} onChange={e => setSickNotify(e.target.checked)} className="mt-0.5 w-4 h-4 accent-rose-600" />
                  <span className="text-sm text-slate-700">
                    <span className="font-semibold">Email affected clients</span>
                    <span className="block text-[12px] text-slate-500 mt-0.5">Sends a reschedule notice to clients with an email on file (via your SendGrid integration). SMS coming soon.</span>
                  </span>
                </label>
                <div className="flex gap-2 justify-end pt-1">
                  <Button variant="outline" onClick={() => setSickDayTarget(null)} data-testid="sick-cancel-btn" className="rounded-full">Cancel</Button>
                  <Button onClick={confirmSickDay} disabled={sickSubmitting} data-testid="sick-confirm-btn" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white">
                    <Thermometer className="w-4 h-4 mr-1.5" /> {sickSubmitting ? "Marking…" : "Mark sick day"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Reschedule queue dialog */}
      <Dialog open={showRescheduleQueue} onOpenChange={setShowRescheduleQueue}>
        <DialogContent className="max-w-2xl" data-testid="reschedule-queue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-rose-600" /> Needs rescheduling ({rescheduleQueue.length})
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-slate-500 -mt-2 font-['Manrope']">Clients flagged when a stylist was marked out sick. Rebook them, then tap Done.</p>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-2 mt-1">
            {rescheduleQueue.length === 0 ? (
              <p className="text-sm text-slate-500 py-10 text-center">Nothing to reschedule right now.</p>
            ) : rescheduleQueue.map(item => (
              <div key={item.appointment_id} data-testid={`queue-item-${item.appointment_id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{item.client_name || "Client"}</p>
                  <p className="text-[12px] text-slate-500 truncate">{(item.service_names || []).join(", ") || "Service"} · with {item.provider_name || "stylist"}</p>
                  <p className="text-[11px] text-slate-400">
                    {item.start_time ? format(parseISO(item.start_time), "EEE, MMM d · h:mm a") : ""}
                    {item.client_phone ? ` · ${item.client_phone}` : ""}
                    {item.client_email ? ` · ${item.client_email}` : ""}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="rounded-full flex-shrink-0" data-testid={`queue-resolve-${item.appointment_id}`} onClick={() => resolveQueueItem(item.appointment_id)}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Done
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
